/**
 * DOAMatrix — Per-(entity, category) Delegation of Authority configuration.
 *
 * Phase 4 of the multi-entity DOA refactor. Lets an admin configure DOA
 * limits broken down by:
 *   - Legal entity (org-wide default + per-entity overrides)
 *   - Transaction category (Default + 12 well-known categories + custom)
 *   - Approval level (L2..L7)
 *
 * Each cell is one row in org_configuration with key DOA_{CATEGORY}_{LEVEL}
 * (or DOA_{LEVEL} for the Default category) and an entity_code that's
 * either NULL (org-wide) or a specific entity code.
 *
 * Reads cascade through:
 *   entity-specific category -> org-wide category ->
 *   entity-specific generic -> org-wide generic -> PLATFORM_DEFAULTS
 *
 * Admin-only — non-admins get a 403 from the PUT/DELETE endpoints.
 */

import { useCallback, useEffect, useState } from 'react';
import api from '../api';
import { API_V1, authFetch, DOA_CATEGORIES, formatAmount } from '../constants';
import { useEntities } from '../hooks/useEntities';
import PageLayout from '../components/ui/PageLayout';
import ActionButton from '../components/ui/ActionButton';

const LEVELS = ['L2', 'L3', 'L4', 'L5', 'L6', 'L7'] as const;
type Level = typeof LEVELS[number];

// Backend uses 999_999_999_999 as the L7 "unlimited" sentinel when it can't
// return NULL (e.g. some cascade paths coalesce). Treat any value at or above
// this as unlimited so the UI renders "—" instead of "$1000.0B".
const UNLIMITED_SENTINEL = 999_999_999_000;

const LEVEL_LABELS: Record<Level, string> = {
  L2: 'L2 Analyst',
  L3: 'L3 Supervisor',
  L4: 'L4 Manager',
  L5: 'L5 Director',
  L6: 'L6 VP / CFO',
  L7: 'L7 Board / Admin',
};

// Category code "DEFAULT" maps to the generic DOA_{LEVEL} keys (no category
// segment). Every other code maps to DOA_{CODE}_{LEVEL}.
const DEFAULT_CATEGORY_CODE = 'DEFAULT';

interface MatrixRow {
  code: string;          // 'DEFAULT' or 'CONTRACT' etc.
  label: string;         // Display label
  isCustom: boolean;     // Not in DOA_CATEGORIES — admin-added
  values: Record<Level, number | null>;  // current effective value per level
  hasOverride: Record<Level, boolean>;   // true if there's an entity/category row at this exact (entity, category, level)
}

// Build a config_key for storage
function configKey(category: string, level: Level): string {
  if (category === DEFAULT_CATEGORY_CODE) return `DOA_${level}`;
  return `DOA_${category}_${level}`;
}

// Validate a custom category name per Option B regex agreed in planning:
//   ^[A-Z][A-Z0-9_]{0,29}$  AND must NOT match ^L[0-9]$
function validateCategoryCode(code: string): string | null {
  if (!code) return 'Category code is required';
  if (!/^[A-Z][A-Z0-9_]{0,29}$/.test(code)) {
    return 'Must be uppercase letters, digits, underscores. Start with a letter. Max 30 chars.';
  }
  if (/^L[0-9]$/.test(code)) {
    return 'Cannot use level codes (L2..L9) as a category name — too confusing';
  }
  return null;
}

// Convert SNAKE_CASE to Title Case for the display label
function snakeToTitle(code: string): string {
  return code
    .split('_')
    .map(w => w.charAt(0) + w.slice(1).toLowerCase())
    .join(' ');
}

export default function DOAMatrix() {
  const entities = useEntities();
  const [selectedEntity, setSelectedEntity] = useState<string | null>(null); // null = org-wide default view
  const [rows, setRows] = useState<MatrixRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);

  // Inline cell edit state. ``editOriginal`` snapshots the value at the moment
  // the cell was clicked so that ``saveEdit`` can short-circuit on blur when
  // the user didn't actually change anything (Phase 4 UI bug bundle: previously
  // every blur triggered a PUT, creating orphan entity_code rows from a
  // simple click-and-look interaction).
  const [editing, setEditing] = useState<{ category: string; level: Level } | null>(null);
  const [editValue, setEditValue] = useState('');
  const [editOriginal, setEditOriginal] = useState<string>('');

  // + Add custom category modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [newCategoryCode, setNewCategoryCode] = useState('');
  const [newCategoryError, setNewCategoryError] = useState<string | null>(null);

  // Reset confirmation
  const [confirmReset, setConfirmReset] = useState<{ category: string; level: Level } | null>(null);

  const showToast = (msg: string, ok: boolean) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 4000);
  };

  // ── Data fetch ──
  // Loads:
  //  1. /doa-categories (custom categories the admin has added)
  //  2. For each row (Default + 12 starter codes + custom): /doa-levels?entity_code=&category=
  //     This gives the EFFECTIVE values after the cascade.
  //  3. For each row + level, query the SAME tuple WITHOUT entity_code to detect
  //     if the value is an entity-specific override or inherited.
  const loadMatrix = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Step 1 — discover custom categories
      let customCodes: string[] = [];
      try {
        const catRes = await authFetch(
          `${API_V1}/org/configuration/doa-categories${selectedEntity ? `?entity_code=${encodeURIComponent(selectedEntity)}` : ''}`,
        );
        if (catRes.ok) {
          const data = await catRes.json();
          customCodes = Array.isArray(data?.categories) ? data.categories : [];
        }
      } catch { /* empty */ }

      // Step 2 — assemble the row list: Default first, then DOA_CATEGORIES,
      // then any custom codes the endpoint returned that aren't already in
      // the default list.
      const defaultCodes = new Set(DOA_CATEGORIES.map(c => c.code));
      const customNotInDefaults = customCodes.filter(c => !defaultCodes.has(c));

      const rowSpecs: { code: string; label: string; isCustom: boolean }[] = [
        { code: DEFAULT_CATEGORY_CODE, label: 'Default (catch-all)', isCustom: false },
        ...DOA_CATEGORIES.map(c => ({ code: c.code, label: c.label, isCustom: false })),
        ...customNotInDefaults.map(c => ({ code: c, label: snakeToTitle(c), isCustom: true })),
      ];

      // Step 3 — for each row, fetch the effective cascade and the org-wide
      // baseline (when an entity is selected) so we can flag overrides.
      const fetches = rowSpecs.map(async (spec) => {
        const params = new URLSearchParams();
        if (selectedEntity) params.set('entity_code', selectedEntity);
        if (spec.code !== DEFAULT_CATEGORY_CODE) params.set('category', spec.code);
        const qs = params.toString() ? `?${params.toString()}` : '';

        const eff = await authFetch(`${API_V1}/org/configuration/doa-levels${qs}`)
          .then(r => r.ok ? r.json() : null)
          .catch(() => null);

        // Compare against org-wide (no entity_code) to detect entity overrides
        let baseline: Record<string, number | null> | null = null;
        if (selectedEntity) {
          const baseParams = new URLSearchParams();
          if (spec.code !== DEFAULT_CATEGORY_CODE) baseParams.set('category', spec.code);
          const bqs = baseParams.toString() ? `?${baseParams.toString()}` : '';
          baseline = await authFetch(`${API_V1}/org/configuration/doa-levels${bqs}`)
            .then(r => r.ok ? r.json() : null)
            .catch(() => null);
        }

        const values: Record<Level, number | null> = {} as any;
        const hasOverride: Record<Level, boolean> = {} as any;
        for (const lvl of LEVELS) {
          const v = eff?.[lvl] ?? null;
          values[lvl] = v;
          if (selectedEntity && baseline) {
            // Override iff the effective value differs from the org-wide baseline
            hasOverride[lvl] = baseline[lvl] !== v;
          } else {
            hasOverride[lvl] = false;
          }
        }
        return { ...spec, values, hasOverride } as MatrixRow;
      });

      const results = await Promise.all(fetches);

      // Phase 4 UI bug bundle (#3): in org-wide view, compute hasOverride
      // by comparing each non-Default row against the Default row at the
      // same level. The cascade engine returns the same value for both rows
      // when a category has no DOA_{CAT}_{LVL} of its own, so any divergence
      // means there IS a category-specific row in org_configuration.
      // Without this pass, override badges and the row Reset button never
      // appear in org-wide view, so an admin can't clean up an accidental
      // category override (e.g. the orphan DOA_CONTRACT_L6=$10M from the
      // Phase 4 click-on-blur bug).
      if (!selectedEntity) {
        const defaultRow = results.find(r => r.code === DEFAULT_CATEGORY_CODE);
        if (defaultRow) {
          for (const r of results) {
            if (r.code === DEFAULT_CATEGORY_CODE) continue;
            for (const lvl of LEVELS) {
              const rv = r.values[lvl];
              const dv = defaultRow.values[lvl];
              // Treat the unlimited sentinel as null for comparison so that
              // a $999B sentinel cell isn't flagged as differing from a NULL
              // default cell that means the same thing.
              const rNorm = (rv !== null && rv !== undefined && rv >= UNLIMITED_SENTINEL) ? null : rv;
              const dNorm = (dv !== null && dv !== undefined && dv >= UNLIMITED_SENTINEL) ? null : dv;
              r.hasOverride[lvl] = rNorm !== dNorm;
            }
          }
        }
      }

      setRows(results);
    } catch (e: any) {
      setError(e?.message || 'Failed to load DOA matrix');
    } finally {
      setLoading(false);
    }
  }, [selectedEntity]);

  useEffect(() => {
    loadMatrix();
  }, [loadMatrix]);

  // ── Cell edit ──
  const startEdit = (category: string, level: Level, currentValue: number | null) => {
    const asString = currentValue !== null && currentValue !== undefined ? String(currentValue) : '';
    setEditing({ category, level });
    setEditValue(asString);
    setEditOriginal(asString);
  };

  const cancelEdit = () => {
    setEditing(null);
    setEditValue('');
    setEditOriginal('');
  };

  const saveEdit = async () => {
    if (!editing) return;
    const trimmed = editValue.trim();
    // Phase 4 UI bug bundle: skip the PUT entirely when the user didn't change
    // anything (e.g. clicked the cell to look at it, then clicked away). This
    // prevents the accidental orphan-row bug where a click-and-blur created
    // a per-entity row with the inherited value.
    if (trimmed === editOriginal.trim()) {
      cancelEdit();
      return;
    }
    if (!trimmed) {
      showToast('Value cannot be empty — use Reset to clear', false);
      return;
    }
    const numeric = Number(trimmed);
    if (!Number.isFinite(numeric) || numeric < 0) {
      showToast('Value must be a non-negative number', false);
      return;
    }
    const key = configKey(editing.category, editing.level);
    try {
      await api.put(`/org/configuration/${key}`, {
        value: String(numeric),
        value_type: 'NUMERIC',
        description: `DOA matrix edit: ${editing.category} ${editing.level}${selectedEntity ? ` for ${selectedEntity}` : ''}`,
        entity_code: selectedEntity || null,
      });
      showToast(`Saved ${key}${selectedEntity ? ` for ${selectedEntity}` : ''}`, true);
      setEditing(null);
      setEditValue('');
      setEditOriginal('');
      // Refresh just this row would be ideal — full refresh is simpler and
      // the matrix is small (~13 rows × 6 levels = 78 cells)
      loadMatrix();
    } catch (e: any) {
      const detail = e?.response?.data?.detail || 'Save failed';
      showToast(detail, false);
    }
  };

  // ── Reset (DELETE the row) ──
  const requestReset = (category: string, level: Level) => {
    setConfirmReset({ category, level });
  };

  const performReset = async () => {
    if (!confirmReset) return;
    const key = configKey(confirmReset.category, confirmReset.level);
    try {
      const url = selectedEntity
        ? `/org/configuration/${key}?entity_code=${encodeURIComponent(selectedEntity)}`
        : `/org/configuration/${key}`;
      await api.delete(url);
      showToast(`Reset ${key}${selectedEntity ? ` for ${selectedEntity}` : ''}`, true);
      setConfirmReset(null);
      loadMatrix();
    } catch (e: any) {
      if (e?.response?.status === 404) {
        showToast('Already at default — nothing to reset', true);
        setConfirmReset(null);
      } else {
        showToast(e?.response?.data?.detail || 'Reset failed', false);
      }
    }
  };

  // ── + Add custom category ──
  const submitNewCategory = () => {
    const code = newCategoryCode.trim().toUpperCase();
    const err = validateCategoryCode(code);
    if (err) {
      setNewCategoryError(err);
      return;
    }
    // Check duplicate against existing rows
    if (rows.some(r => r.code === code)) {
      setNewCategoryError(`Category '${code}' already exists`);
      return;
    }
    // Add empty row to local state — no backend write until a value is set
    setRows(prev => [
      ...prev,
      {
        code,
        label: snakeToTitle(code),
        isCustom: true,
        values: { L2: null, L3: null, L4: null, L5: null, L6: null, L7: null },
        hasOverride: { L2: false, L3: false, L4: false, L5: false, L6: false, L7: false },
      },
    ]);
    setShowAddModal(false);
    setNewCategoryCode('');
    setNewCategoryError(null);
    showToast(`Added category ${code}. Set any value to persist.`, true);
  };

  // ── Render ──
  return (
    <PageLayout
      title="DOA Matrix"
      subtitle="Configure approval limits per legal entity, per transaction category, per level. Click a cell to edit. L7 is treated as unlimited."
    >
      {/* Entity selector */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16,
        padding: '12px 16px',
        background: 'var(--bg-card)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 8,
      }}>
        <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-hint)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
          Entity:
        </label>
        <select
          value={selectedEntity || ''}
          onChange={e => setSelectedEntity(e.target.value || null)}
          style={{
            padding: '6px 12px', borderRadius: 6, fontSize: 13,
            border: '1px solid var(--border-default)',
            background: 'var(--bg-card)', color: 'var(--text-primary)',
            minWidth: 240, cursor: 'pointer',
          }}
        >
          <option value="">All entities (org-wide default)</option>
          {entities.map(ent => (
            <option key={ent.entity_code} value={ent.entity_code}>
              {ent.entity_code} — {ent.entity_name} ({ent.functional_currency})
            </option>
          ))}
        </select>
        <span style={{ fontSize: 11, color: 'var(--text-hint)', marginLeft: 'auto' }}>
          {selectedEntity
            ? `Showing overrides for ${selectedEntity}. Italics = inherited from "All entities". Bold ★ = entity override. Click cell to edit.`
            : `Showing org-wide defaults. Bold ★ = category-specific override (differs from Default row). Switch to a specific entity to add per-entity overrides.`}
        </span>
      </div>

      {/* Action bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div style={{ fontSize: 11, color: 'var(--text-hint)' }}>
          {rows.length} categor{rows.length === 1 ? 'y' : 'ies'} × 6 levels
          {' · '}All amounts in the org reporting currency
        </div>
        <ActionButton
          label="+ Add Category"
          onClick={() => { setShowAddModal(true); setNewCategoryCode(''); setNewCategoryError(null); }}
          variant="secondary"
          size="sm"
        />
      </div>

      {error && (
        <div style={{
          padding: 12, marginBottom: 12, borderRadius: 6,
          background: 'rgba(239,68,68,0.1)', color: 'var(--red)',
          border: '1px solid var(--red)', fontSize: 13,
        }}>
          {error}
        </div>
      )}

      {/* Matrix table */}
      <div style={{
        background: 'var(--bg-card)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 8, overflow: 'hidden',
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={thStyle}>Category</th>
              {LEVELS.map(lvl => (
                <th key={lvl} style={{ ...thStyle, textAlign: 'right' }}>
                  {LEVEL_LABELS[lvl]}
                </th>
              ))}
              <th style={{ ...thStyle, width: 60 }}></th>
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 ? (
              <tr><td colSpan={8} style={{ padding: 40, textAlign: 'center', color: 'var(--text-hint)' }}>Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={8} style={{ padding: 40, textAlign: 'center', color: 'var(--text-hint)' }}>No DOA categories configured.</td></tr>
            ) : rows.map(row => (
              <tr key={row.code}>
                <td style={tdStyle}>
                  <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                    {row.label}
                    {row.isCustom && <span style={{ marginLeft: 6, fontSize: 9, padding: '2px 6px', background: 'var(--bg-hover)', color: 'var(--text-hint)', borderRadius: 3, fontWeight: 700 }}>CUSTOM</span>}
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--text-hint)', fontFamily: 'monospace' }}>{row.code}</div>
                </td>
                {LEVELS.map(lvl => {
                  const val = row.values[lvl];
                  const isOverride = row.hasOverride[lvl];
                  const isEditing = editing?.category === row.code && editing?.level === lvl;
                  // Phase 4 UI bug bundle (#1): treat the backend unlimited
                  // sentinel (~$1T) as Unlimited so cells render "—" instead
                  // of "$1000.0B".
                  const isUnlimited = val === null || val === undefined || val >= UNLIMITED_SENTINEL;

                  if (isEditing) {
                    return (
                      <td key={lvl} style={{ ...tdStyle, textAlign: 'right' }}>
                        <input
                          autoFocus
                          type="number"
                          min="0"
                          step="any"
                          value={editValue}
                          onChange={e => setEditValue(e.target.value)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') saveEdit();
                            if (e.key === 'Escape') cancelEdit();
                          }}
                          onBlur={saveEdit}
                          style={{
                            width: 110, padding: '4px 6px', fontSize: 12,
                            textAlign: 'right',
                            border: '1px solid var(--accent)', borderRadius: 4,
                            background: 'var(--bg-card)', color: 'var(--text-primary)',
                          }}
                        />
                      </td>
                    );
                  }
                  return (
                    <td
                      key={lvl}
                      style={{
                        ...tdStyle,
                        textAlign: 'right',
                        cursor: 'pointer',
                        fontWeight: isOverride ? 700 : 400,
                        fontStyle: !isOverride && selectedEntity ? 'italic' : 'normal',
                        color: isUnlimited
                          ? 'var(--text-hint)'
                          : (isOverride ? 'var(--accent)' : 'var(--text-secondary)'),
                      }}
                      title={
                        isUnlimited
                          ? 'Unlimited (no limit set)'
                          : isOverride
                            ? (selectedEntity
                                ? `Entity override for ${selectedEntity}: $${val?.toLocaleString()}`
                                : `Category override (differs from Default row): $${val?.toLocaleString()}`)
                            : (selectedEntity
                                ? `Inherited from org-wide value: $${val?.toLocaleString()}`
                                : `Inherits from Default row: $${val?.toLocaleString()}`)
                      }
                      onClick={() => startEdit(row.code, lvl, val)}
                    >
                      {isUnlimited ? '—' : formatAmount(val as number)}
                      {isOverride && <span style={{ marginLeft: 4, fontSize: 9 }}>★</span>}
                    </td>
                  );
                })}
                <td style={{ ...tdStyle, textAlign: 'center' }}>
                  {LEVELS.some(l => row.hasOverride[l]) && (
                    <button
                      type="button"
                      onClick={() => {
                        const lvl = LEVELS.find(l => row.hasOverride[l]) as Level;
                        if (lvl) requestReset(row.code, lvl);
                      }}
                      title={selectedEntity
                        ? `Reset the first entity override on this row (${LEVELS.find(l => row.hasOverride[l])})`
                        : `Reset the first category override on this row (${LEVELS.find(l => row.hasOverride[l])})`}
                      style={{
                        padding: '4px 10px', fontSize: 11, borderRadius: 4,
                        background: 'transparent', color: 'var(--text-hint)',
                        border: '1px solid var(--border-default)', cursor: 'pointer',
                      }}
                    >
                      Reset
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* + Add custom category modal */}
      {showAddModal && (
        <div style={modalBackdropStyle} onClick={() => setShowAddModal(false)}>
          <div style={modalStyle} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>
              Add custom DOA category
            </h3>
            <p style={{ fontSize: 12, color: 'var(--text-hint)', margin: '0 0 12px', lineHeight: 1.5 }}>
              Uppercase letters, digits, and underscores. Must start with a letter. Max 30 characters.
              Examples: <code>BANK_GUARANTEE</code>, <code>CYBER_INSURANCE</code>, <code>INVENTORY_DISPOSAL</code>.
            </p>
            <input
              autoFocus
              type="text"
              value={newCategoryCode}
              onChange={e => {
                setNewCategoryCode(e.target.value.toUpperCase());
                setNewCategoryError(null);
              }}
              onKeyDown={e => { if (e.key === 'Enter') submitNewCategory(); if (e.key === 'Escape') setShowAddModal(false); }}
              placeholder="BANK_GUARANTEE"
              style={{
                width: '100%', padding: '8px 12px', fontSize: 13,
                border: `1px solid ${newCategoryError ? 'var(--red)' : 'var(--border-default)'}`,
                borderRadius: 6, background: 'var(--bg-card)', color: 'var(--text-primary)',
                fontFamily: 'monospace', textTransform: 'uppercase',
              }}
            />
            {newCategoryCode && !newCategoryError && (
              <div style={{ marginTop: 6, fontSize: 11, color: 'var(--text-hint)' }}>
                Display label: <strong>{snakeToTitle(newCategoryCode)}</strong>
              </div>
            )}
            {newCategoryError && (
              <div style={{ marginTop: 6, fontSize: 11, color: 'var(--red)' }}>
                {newCategoryError}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <ActionButton label="Cancel" onClick={() => setShowAddModal(false)} variant="secondary" size="sm" />
              <ActionButton label="Add" onClick={submitNewCategory} variant="primary" size="sm" />
            </div>
          </div>
        </div>
      )}

      {/* Reset confirmation modal */}
      {confirmReset && (
        <div style={modalBackdropStyle} onClick={() => setConfirmReset(null)}>
          <div style={modalStyle} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>
              Reset DOA cell?
            </h3>
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: '0 0 12px', lineHeight: 1.5 }}>
              Remove the {selectedEntity ? `${selectedEntity}-specific override` : 'org-wide value'} for{' '}
              <code style={{ fontFamily: 'monospace', fontSize: 12 }}>
                {configKey(confirmReset.category, confirmReset.level)}
              </code>
              ?
            </p>
            <p style={{ fontSize: 11, color: 'var(--text-hint)', margin: '0 0 16px' }}>
              {selectedEntity
                ? `After reset, this cell will inherit from the org-wide value (or platform default if none).`
                : `After reset, this cell will inherit from the platform default.`}
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <ActionButton label="Cancel" onClick={() => setConfirmReset(null)} variant="secondary" size="sm" />
              <ActionButton label="Reset" onClick={performReset} variant="danger" size="sm" />
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', top: 20, right: 20, zIndex: 1000,
          padding: '12px 18px', borderRadius: 8,
          background: toast.ok ? 'var(--green)' : 'var(--red)',
          color: '#fff', fontSize: 13, fontWeight: 600,
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        }}>
          {toast.msg}
        </div>
      )}
    </PageLayout>
  );
}

const thStyle: React.CSSProperties = {
  padding: '10px 14px', fontSize: 11, fontWeight: 600,
  textTransform: 'uppercase', letterSpacing: '0.04em',
  color: 'var(--text-hint)', borderBottom: '2px solid var(--border-subtle)',
  background: 'var(--bg-card)', whiteSpace: 'nowrap',
  textAlign: 'left',
};

const tdStyle: React.CSSProperties = {
  padding: '10px 14px', fontSize: 13, color: 'var(--text-primary)',
  borderBottom: '1px solid var(--border-subtle)',
  whiteSpace: 'nowrap',
};

const modalBackdropStyle: React.CSSProperties = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
  background: 'rgba(0,0,0,0.4)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 999,
};

const modalStyle: React.CSSProperties = {
  background: 'var(--bg-card)',
  border: '1px solid var(--border-default)',
  borderRadius: 12,
  padding: 24,
  width: 460,
  maxWidth: '90vw',
  boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
};
